import { relations } from "drizzle-orm";
import { index, integer, real, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { series, entries, entryMedia } from "./content";

/* -------------------------------------------------------------------------- */
/*  entities — canonical entity record                                      */
/* -------------------------------------------------------------------------- */
export const entities = sqliteTable(
  "entities",
  {
    id: text("id").primaryKey(),
    slug: text("slug").notNull(),
    seriesId: text("series_id")
      .notNull()
      .references(() => series.id, { onDelete: "cascade" }),
    type: text("type", {
      enum: [
        "character",
        "location",
        "organization",
        "item",
        "concept",
        "event",
        "ability",
        "title",
        "other",
      ],
    })
      .notNull()
      .default("other"),
    canonicalName: text("canonical_name").notNull(),
    aliases: text("aliases").notNull().default("[]"),
    shortSummary: text("short_summary"),
    imageR2Key: text("image_r2_key"),
    metadata: text("metadata").notNull().default("{}"),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
    updatedAt: integer("updated_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .$onUpdate(() => new Date())
      .notNull(),
  },
  (table) => [
    uniqueIndex("entities_series_slug_idx").on(table.seriesId, table.slug),
    index("entities_series_type_idx").on(table.seriesId, table.type),
    index("entities_canonical_name_idx").on(table.canonicalName),
  ],
);

export type Entity = typeof entities.$inferSelect;

/* -------------------------------------------------------------------------- */
/*  entity_facts — per-entry knowledge facts with feedforward               */
/* -------------------------------------------------------------------------- */
export const entityFacts = sqliteTable(
  "entity_facts",
  {
    id: text("id").primaryKey(),
    entityId: text("entity_id")
      .notNull()
      .references(() => entities.id, { onDelete: "cascade" }),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    kind: text("kind", {
      enum: [
        "canon",
        "relationship",
        "plot",
        "status",
        "world",
        "open_thread",
        "translation_hazard",
      ],
    }).notNull(),
    summary: text("summary").notNull(),
    tags: text("tags").notNull().default("[]"),
    importance: integer("importance").notNull().default(3),
    sourceFactIds: text("source_fact_ids").notNull().default("[]"),
    confidence: real("confidence").notNull().default(1.0),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => [
    index("entity_facts_entity_entry_idx").on(table.entityId, table.entryId),
    index("entity_facts_entry_idx").on(table.entryId),
    index("entity_facts_entity_kind_idx").on(table.entityId, table.kind),
  ],
);

export type EntityFact = typeof entityFacts.$inferSelect;

/* -------------------------------------------------------------------------- */
/*  entity_relations — adjacency-list relationship edges                    */
/* -------------------------------------------------------------------------- */
export const entityRelations = sqliteTable(
  "entity_relations",
  {
    id: text("id").primaryKey(),
    sourceEntityId: text("source_entity_id")
      .notNull()
      .references(() => entities.id, { onDelete: "cascade" }),
    targetEntityId: text("target_entity_id")
      .notNull()
      .references(() => entities.id, { onDelete: "cascade" }),
    relation: text("relation", {
      enum: [
        "friend_of",
        "enemy_of",
        "family_of",
        "member_of",
        "located_in",
        "owns",
        "created",
        "wielder_of",
        "part_of",
        "related_to",
      ],
    }).notNull(),
    fact: text("fact"),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    validFromEntryId: text("valid_from_entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    validUntilEntryId: text("valid_until_entry_id").references(() => entries.id, {
      onDelete: "set null",
    }),
    confidence: real("confidence").notNull().default(1.0),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => [
    index("entity_relations_source_idx").on(table.sourceEntityId),
    index("entity_relations_target_idx").on(table.targetEntityId),
    index("entity_relations_type_idx").on(table.relation),
    index("entity_relations_valid_range_idx").on(table.validFromEntryId, table.validUntilEntryId),
  ],
);

export type EntityRelation = typeof entityRelations.$inferSelect;

/* -------------------------------------------------------------------------- */
/*  entity_mentions_text — pre-computed character offsets in entry text     */
/* -------------------------------------------------------------------------- */
export const entityMentionsText = sqliteTable(
  "entity_mentions_text",
  {
    id: text("id").primaryKey(),
    entityId: text("entity_id")
      .notNull()
      .references(() => entities.id, { onDelete: "cascade" }),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    startPos: integer("start_pos").notNull(),
    endPos: integer("end_pos").notNull(),
    text: text("text").notNull(),
    confidence: real("confidence").notNull().default(1.0),
  },
  (table) => [
    index("entity_mentions_text_entry_pos_idx").on(table.entryId, table.startPos),
    index("entity_mentions_text_entity_entry_idx").on(table.entityId, table.entryId),
  ],
);

export type EntityMentionText = typeof entityMentionsText.$inferSelect;

/* -------------------------------------------------------------------------- */
/*  entity_mentions_image — IIIF-compatible normalized regions              */
/* -------------------------------------------------------------------------- */
export const entityMentionsImage = sqliteTable(
  "entity_mentions_image",
  {
    id: text("id").primaryKey(),
    entityId: text("entity_id")
      .notNull()
      .references(() => entities.id, { onDelete: "cascade" }),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    entryMediaId: text("entry_media_id")
      .notNull()
      .references(() => entryMedia.id, { onDelete: "cascade" }),
    x: real("x").notNull(),
    y: real("y").notNull(),
    width: real("width").notNull(),
    height: real("height").notNull(),
    label: text("label"),
    confidence: real("confidence").notNull().default(1.0),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => [
    index("entity_mentions_image_media_idx").on(table.entryMediaId),
    index("entity_mentions_image_entity_entry_idx").on(table.entityId, table.entryId),
  ],
);

export type EntityMentionImage = typeof entityMentionsImage.$inferSelect;

/* -------------------------------------------------------------------------- */
/*  entity_mentions_video — time-range appearances in video stream          */
/* -------------------------------------------------------------------------- */
export const entityMentionsVideo = sqliteTable(
  "entity_mentions_video",
  {
    id: text("id").primaryKey(),
    entityId: text("entity_id")
      .notNull()
      .references(() => entities.id, { onDelete: "cascade" }),
    entryId: text("entry_id")
      .notNull()
      .references(() => entries.id, { onDelete: "cascade" }),
    entryMediaId: text("entry_media_id")
      .notNull()
      .references(() => entryMedia.id, { onDelete: "cascade" }),
    timestampIn: real("timestamp_in").notNull(),
    timestampOut: real("timestamp_out"),
    label: text("label"),
    confidence: real("confidence").notNull().default(1.0),
    createdAt: integer("created_at", { mode: "timestamp_ms" })
      .$defaultFn(() => new Date())
      .notNull(),
  },
  (table) => [
    index("entity_mentions_video_media_time_idx").on(table.entryMediaId, table.timestampIn),
    index("entity_mentions_video_entity_entry_idx").on(table.entityId, table.entryId),
  ],
);

export type EntityMentionVideo = typeof entityMentionsVideo.$inferSelect;

/* -------------------------------------------------------------------------- */
/*  entity_search_content — D1-level entity search cache                    */
/* -------------------------------------------------------------------------- */
export const entitySearchContent = sqliteTable("entity_search_content", {
  entityId: text("entity_id")
    .primaryKey()
    .references(() => entities.id, { onDelete: "cascade" }),
  searchableText: text("searchable_text").notNull(),
  indexedAt: integer("indexed_at", { mode: "timestamp_ms" })
    .$defaultFn(() => new Date())
    .notNull(),
});

export type EntitySearchContent = typeof entitySearchContent.$inferSelect;

/* -------------------------------------------------------------------------- */
/*  relations                                                                */
/* -------------------------------------------------------------------------- */
export const entitiesRelations = relations(entities, ({ one, many }) => ({
  series: one(series, {
    fields: [entities.seriesId],
    references: [series.id],
  }),
  facts: many(entityFacts),
  mentionsText: many(entityMentionsText),
  mentionsImage: many(entityMentionsImage),
  mentionsVideo: many(entityMentionsVideo),
  outboundRelations: many(entityRelations, { relationName: "source_entity" }),
  inboundRelations: many(entityRelations, { relationName: "target_entity" }),
}));

export const entityFactsRelations = relations(entityFacts, ({ one }) => ({
  entity: one(entities, {
    fields: [entityFacts.entityId],
    references: [entities.id],
  }),
  entry: one(entries, {
    fields: [entityFacts.entryId],
    references: [entries.id],
  }),
}));

export const entityRelationsRelations = relations(entityRelations, ({ one }) => ({
  sourceEntity: one(entities, {
    fields: [entityRelations.sourceEntityId],
    references: [entities.id],
    relationName: "source_entity",
  }),
  targetEntity: one(entities, {
    fields: [entityRelations.targetEntityId],
    references: [entities.id],
    relationName: "target_entity",
  }),
  entry: one(entries, {
    fields: [entityRelations.entryId],
    references: [entries.id],
  }),
  validFromEntry: one(entries, {
    fields: [entityRelations.validFromEntryId],
    references: [entries.id],
  }),
  validUntilEntry: one(entries, {
    fields: [entityRelations.validUntilEntryId],
    references: [entries.id],
  }),
}));

export const entityMentionsTextRelations = relations(entityMentionsText, ({ one }) => ({
  entity: one(entities, {
    fields: [entityMentionsText.entityId],
    references: [entities.id],
  }),
  entry: one(entries, {
    fields: [entityMentionsText.entryId],
    references: [entries.id],
  }),
}));

export const entityMentionsImageRelations = relations(entityMentionsImage, ({ one }) => ({
  entity: one(entities, {
    fields: [entityMentionsImage.entityId],
    references: [entities.id],
  }),
  entry: one(entries, {
    fields: [entityMentionsImage.entryId],
    references: [entries.id],
  }),
  media: one(entryMedia, {
    fields: [entityMentionsImage.entryMediaId],
    references: [entryMedia.id],
  }),
}));

export const entityMentionsVideoRelations = relations(entityMentionsVideo, ({ one }) => ({
  entity: one(entities, {
    fields: [entityMentionsVideo.entityId],
    references: [entities.id],
  }),
  entry: one(entries, {
    fields: [entityMentionsVideo.entryId],
    references: [entries.id],
  }),
  media: one(entryMedia, {
    fields: [entityMentionsVideo.entryMediaId],
    references: [entryMedia.id],
  }),
}));
